import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, titleStyle, readoutStyle, FONT, px } from '../theme';
import { createButton } from '../ui/button';
import type { Campaign } from '@core/campaign/campaign';
import { maybeShowTutorial, createAdviserButton } from '../ui/adviserPanel';
import { createSettingsButton } from '../ui/settingsPanel';
import { addBackdrop, EVIDENCE_BACKDROP } from '../assets';

/**
 * Die Beweislage.
 *
 * Bewusst **ohne jede Wertung**: keine Balken, keine Rangfolge, kein Hinweis
 * darauf, wofuer die Funde sprechen. Das Spiel legt die Fundstuecke vor und
 * schweigt — das Kombinieren ist die Aufgabe der Spielerin.
 *
 * Eine fruehere Fassung zeigte hier Balken je Aufloesung. Das nahm genau die
 * Denkarbeit ab, fuer die das ganze Mysterium gebaut wurde: Man sammelte, bis
 * ein Balken lang genug war, statt zu lesen und zu schliessen.
 *
 * Was bleibt, ist Ordnung ohne Deutung — Akt und Fundart als Etikett, damit
 * man die eigenen Notizen sortieren kann.
 */
export class EvidenceScene extends Phaser.Scene {
  constructor() {
    super('Evidence');
  }

  create(): void {
    const campaign = this.registry.get('campaign') as Campaign;
    this.cameras.main.setBackgroundColor(PALETTE.abyss);

    // Eine alte Seekarte als Unterlage. Sehr schwach, denn hier wird gelesen —
    // aber sie sagt in einem Bild, was diese Seite ist: ein Schreibtisch, auf
    // dem jemand Funde sortiert, und keine Auswertung.
    addBackdrop(this, EVIDENCE_BACKDROP, 0.12);

    const { width, height } = this.scale;
    const centreX = width / 2;
    const columnWidth = Math.min(700, width - 160);
    const left = centreX - columnWidth / 2;

    this.add.text(left, 36, t('evidence.title'), titleStyle(22));
    createAdviserButton(this, left + columnWidth, 46, campaign, 'evidence');
    createSettingsButton(this, left + columnWidth, 24, () => this.scene.restart());
    maybeShowTutorial(this, campaign, 'evidence');
    this.add.text(left, 68, t('evidence.hint'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
      fontStyle: 'italic',
    });

    const clues = campaign.ledger.all;

    if (clues.length === 0) {
      this.add.text(left, 110, t('evidence.empty'), {
        ...readoutStyle(13),
        color: PALETTE.inkFaint,
        fontStyle: 'italic',
      });
    } else {
      this.renderClues(campaign, left, 104, columnWidth, height);
    }

    this.renderOutstanding(campaign, left, height - 108, columnWidth);

    createButton(this, centreX, height - 44, t('evidence.close'), () => this.scene.start('WorldMap'), {
      width: 180,
      height: 32,
      fontSize: 12,
    });

    this.cameras.main.fadeIn(280, 5, 8, 13);
  }

  private renderClues(
    campaign: Campaign,
    left: number,
    top: number,
    columnWidth: number,
    height: number,
  ): void {
    let y = top;
    let lastAct = 0;

    for (const clue of campaign.ledger.all) {
      if (clue.act !== lastAct) {
        lastAct = clue.act;
        this.add.text(left, y, t('evidence.act', { act: clue.act }), {
          fontFamily: FONT.mono,
          fontSize: px(11),
          color: TEXT_COLOUR.coast,
        });
        y += 20;
      }

      // Fundart und Herkunft als Etikett — das ordnet, ohne zu deuten.
      this.add.text(left + 10, y, t(`slot.${clue.slot}`), {
        fontFamily: FONT.mono,
        fontSize: px(10),
        color: PALETTE.inkFaint,
      });
      this.add
        .text(left + columnWidth, y, t('evidence.from', { source: clue.sourceLabel }), {
          fontFamily: FONT.mono,
          fontSize: px(10),
          color: PALETTE.inkFaint,
        })
        .setOrigin(1, 0);
      y += 15;

      const body = this.add.text(left + 10, y, t(clue.textKey), {
        ...readoutStyle(12),
        wordWrap: { width: columnWidth - 20 },
        lineSpacing: 2,
      });
      y += body.height + 16;

      if (y > height - 140) break;
    }
  }

  /**
   * Was der laufende Akt noch braucht — als Fundart, nicht als Auflösung.
   * Das sagt der Spielerin, wonach sie suchen muss, ohne zu verraten, was sie
   * finden wird: Fundstücke gibt es nur an Wracks.
   */
  private renderOutstanding(
    campaign: Campaign,
    left: number,
    y: number,
    columnWidth: number,
  ): void {
    const outstanding = campaign.outstandingEvidenceKinds;
    if (outstanding.length === 0) return;

    this.add.rectangle(left, y - 12, columnWidth, 1, PALETTE.panelEdge).setOrigin(0, 0.5);

    this.add.text(left, y, t('evidence.outstanding'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    });

    this.add.text(left, y + 18, outstanding.map((slot) => t(`slot.${slot}`)).join('  ·  '), {
      fontFamily: FONT.mono,
      fontSize: px(12),
      color: TEXT_COLOUR.landmark,
    });
  }
}
